import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { customerAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  Calendar, Clock, Users, MapPin, Loader2, CreditCard, 
  CheckCircle, XCircle, AlertTriangle, FileText, ExternalLink,
  Phone, Mail, User, LogOut, ChevronRight, History, Settings,
  Ticket, MessageSquare, Plus, Trash2, Star
} from 'lucide-react';
import { format, parseISO, isPast, isFuture } from 'date-fns';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';

// Initialize Stripe with live publishable key
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

export default function CustomerPortalPage() {
  const { user, isAuthenticated, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [activeTab, setActiveTab] = useState('upcoming');
  
  // Profile state
  const [profile, setProfile] = useState({ name: '', email: '', phone: '' });
  const [savingProfile, setSavingProfile] = useState(false);
  
  // Cancel dialog state
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelBooking, setCancelBooking] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);

  // Payment Methods state
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(false);
  const [showAddCardDialog, setShowAddCardDialog] = useState(false);
  const [addingCard, setAddingCard] = useState(false);
  const [clientSecret, setClientSecret] = useState('');
  const [deletingCardId, setDeletingCardId] = useState(null);

  useEffect(() => {
    // Wait for auth to finish loading before checking authentication
    if (authLoading) return;
    
    if (!isAuthenticated) {
      navigate('/login?redirect=/my-bookings');
      return;
    }
    loadData();
  }, [authLoading, isAuthenticated, navigate]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [bookingsRes, profileRes] = await Promise.all([
        customerAPI.getMyBookings(),
        customerAPI.getProfile()
      ]);
      setBookings(bookingsRes.data || []);
      setProfile(profileRes.data || {});
    } catch (error) {
      console.error('Error loading data:', error);
      if (error.response?.status === 401) {
        navigate('/login?redirect=/my-bookings');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadPaymentMethods = async () => {
    setLoadingPaymentMethods(true);
    try {
      const res = await customerAPI.getPaymentMethods();
      setPaymentMethods(res.data?.payment_methods || []);
    } catch (error) {
      console.error('Error loading payment methods:', error);
      toast.error('Failed to load payment methods');
    } finally {
      setLoadingPaymentMethods(false);
    }
  };

  // Load payment methods when switching to payments tab
  useEffect(() => {
    if (activeTab === 'payments' && isAuthenticated) {
      loadPaymentMethods();
    }
  }, [activeTab, isAuthenticated]);

  const handleAddCard = async () => {
    try {
      const res = await customerAPI.createSetupIntent();
      setClientSecret(res.data.client_secret);
      setShowAddCardDialog(true);
    } catch (error) {
      toast.error('Failed to initialize card setup');
    }
  };

  const handleDeletePaymentMethod = async (pmId) => {
    setDeletingCardId(pmId);
    try {
      await customerAPI.deletePaymentMethod(pmId);
      toast.success('Payment method removed');
      loadPaymentMethods();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove payment method');
    } finally {
      setDeletingCardId(null);
    }
  };

  const handleSetDefaultPaymentMethod = async (pmId) => {
    try {
      await customerAPI.setDefaultPaymentMethod(pmId);
      toast.success('Default payment method updated');
      loadPaymentMethods();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set default');
    }
  };

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      await customerAPI.updateProfile(profile);
      toast.success('Profile updated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleCancelBooking = async () => {
    if (!cancelBooking) return;
    setCancelling(true);
    try {
      await customerAPI.cancelBooking(cancelBooking.id, cancelReason);
      toast.success('Booking cancelled successfully');
      setShowCancelDialog(false);
      setCancelBooking(null);
      setCancelReason('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel booking');
    } finally {
      setCancelling(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  const upcomingBookings = bookings.filter(b => b.is_upcoming && b.status !== 'cancelled');
  const pastBookings = bookings.filter(b => !b.is_upcoming || b.status === 'cancelled');

  const getStatusBadge = (booking) => {
    switch (booking.status) {
      case 'confirmed':
        return <Badge className="bg-green-100 text-green-800">Confirmed</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-800">Pending</Badge>;
      case 'cancelled':
        return <Badge className="bg-red-100 text-red-800">Cancelled</Badge>;
      case 'completed':
        return <Badge className="bg-blue-100 text-blue-800">Completed</Badge>;
      default:
        return <Badge variant="secondary">{booking.status}</Badge>;
    }
  };

  const formatDate = (dateStr) => {
    try {
      return format(parseISO(dateStr), 'MMM d, yyyy');
    } catch {
      return dateStr;
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/" className="text-xl font-bold text-primary">
                Tour Finder
              </Link>
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">My Bookings</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-muted-foreground hidden sm:inline">
                {user?.name || user?.email}
              </span>
              <Button variant="ghost" size="sm" onClick={handleLogout}>
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Profile Card */}
            <Card className="p-6">
              <div className="text-center mb-4">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <User className="h-8 w-8 text-primary" />
                </div>
                <h2 className="font-semibold">{profile.name || 'Guest'}</h2>
                <p className="text-sm text-muted-foreground">{profile.email}</p>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between py-2 border-t">
                  <span className="text-muted-foreground">Total Bookings</span>
                  <span className="font-medium">{bookings.length}</span>
                </div>
                <div className="flex justify-between py-2 border-t">
                  <span className="text-muted-foreground">Upcoming</span>
                  <span className="font-medium text-green-600">{upcomingBookings.length}</span>
                </div>
              </div>
            </Card>

            {/* Contact Info */}
            <Card className="p-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                Contact Tour Finder
              </h3>
              <div className="space-y-3 text-sm">
                <a 
                  href="mailto:tourfinder.puertorico@gmail.com" 
                  className="flex items-center gap-2 text-muted-foreground hover:text-primary"
                >
                  <Mail className="h-4 w-4" />
                  tourfinder.puertorico@gmail.com
                </a>
                <a 
                  href="tel:+17879297723" 
                  className="flex items-center gap-2 text-muted-foreground hover:text-primary"
                >
                  <Phone className="h-4 w-4" />
                  +1 (787) 929-7723
                </a>
              </div>
            </Card>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="mb-6">
                <TabsTrigger value="upcoming" className="gap-2">
                  <Ticket className="h-4 w-4" />
                  Upcoming ({upcomingBookings.length})
                </TabsTrigger>
                <TabsTrigger value="history" className="gap-2">
                  <History className="h-4 w-4" />
                  History ({pastBookings.length})
                </TabsTrigger>
                <TabsTrigger value="payments" className="gap-2" data-testid="payments-tab">
                  <CreditCard className="h-4 w-4" />
                  Payment Methods
                </TabsTrigger>
                <TabsTrigger value="settings" className="gap-2">
                  <Settings className="h-4 w-4" />
                  Settings
                </TabsTrigger>
              </TabsList>

              {/* Upcoming Bookings */}
              <TabsContent value="upcoming">
                {upcomingBookings.length === 0 ? (
                  <Card className="p-12 text-center">
                    <Calendar className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                    <h3 className="text-xl font-semibold mb-2">No Upcoming Bookings</h3>
                    <p className="text-muted-foreground mb-6">
                      Ready for your next adventure? Browse our tours!
                    </p>
                    <Link to="/tours">
                      <Button>Explore Tours</Button>
                    </Link>
                  </Card>
                ) : (
                  <div className="space-y-4">
                    {upcomingBookings.map((booking) => (
                      <BookingCard 
                        key={booking.id} 
                        booking={booking} 
                        onCancel={() => {
                          setCancelBooking(booking);
                          setShowCancelDialog(true);
                        }}
                        formatDate={formatDate}
                        getStatusBadge={getStatusBadge}
                      />
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Booking History */}
              <TabsContent value="history">
                {pastBookings.length === 0 ? (
                  <Card className="p-12 text-center">
                    <History className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                    <h3 className="text-xl font-semibold mb-2">No Booking History</h3>
                    <p className="text-muted-foreground">
                      Your past bookings will appear here.
                    </p>
                  </Card>
                ) : (
                  <div className="space-y-4">
                    {pastBookings.map((booking) => (
                      <BookingCard 
                        key={booking.id} 
                        booking={booking} 
                        isPast={true}
                        formatDate={formatDate}
                        getStatusBadge={getStatusBadge}
                      />
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Payment Methods */}
              <TabsContent value="payments" data-testid="payments-content">
                <Card className="p-6">
                  <div className="flex justify-between items-center mb-6">
                    <h3 className="font-semibold text-lg">Saved Payment Methods</h3>
                    <Button onClick={handleAddCard} data-testid="add-card-btn">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Card
                    </Button>
                  </div>
                  
                  {loadingPaymentMethods ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                    </div>
                  ) : paymentMethods.length === 0 ? (
                    <div className="text-center py-12">
                      <CreditCard className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                      <h4 className="font-semibold mb-2">No Payment Methods Saved</h4>
                      <p className="text-muted-foreground mb-4">
                        Add a card to speed up your future bookings
                      </p>
                      <Button variant="outline" onClick={handleAddCard}>
                        <Plus className="h-4 w-4 mr-2" />
                        Add Your First Card
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {paymentMethods.map((pm) => (
                        <div 
                          key={pm.id} 
                          className={`flex items-center justify-between p-4 rounded-lg border ${pm.is_default ? 'border-primary bg-primary/5' : 'border-border'}`}
                          data-testid={`payment-method-${pm.id}`}
                        >
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-8 bg-gradient-to-br from-gray-800 to-gray-600 rounded flex items-center justify-center text-white text-xs font-bold uppercase">
                              {pm.brand === 'visa' ? 'VISA' : pm.brand === 'mastercard' ? 'MC' : pm.brand?.slice(0,4)}
                            </div>
                            <div>
                              <p className="font-medium flex items-center gap-2">
                                •••• {pm.last4}
                                {pm.is_default && (
                                  <Badge variant="secondary" className="text-xs">Default</Badge>
                                )}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                Expires {pm.exp_month?.toString().padStart(2, '0')}/{pm.exp_year}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {!pm.is_default && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleSetDefaultPaymentMethod(pm.id)}
                              >
                                <Star className="h-4 w-4 mr-1" />
                                Set Default
                              </Button>
                            )}
                            <Button 
                              variant="ghost" 
                              size="icon"
                              className="text-destructive hover:text-destructive"
                              onClick={() => handleDeletePaymentMethod(pm.id)}
                              disabled={deletingCardId === pm.id}
                            >
                              {deletingCardId === pm.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Trash2 className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <div className="mt-6 p-4 bg-muted/50 rounded-lg">
                    <p className="text-sm text-muted-foreground">
                      <strong>🔒 Secure:</strong> Your payment information is securely stored with Stripe. 
                      We never store your full card number.
                    </p>
                  </div>
                </Card>
              </TabsContent>

              {/* Settings */}
              <TabsContent value="settings">
                <Card className="p-6">
                  <h3 className="font-semibold text-lg mb-6">Profile Settings</h3>
                  <div className="space-y-4 max-w-md">
                    <div>
                      <Label>Full Name</Label>
                      <Input 
                        value={profile.name || ''}
                        onChange={(e) => setProfile({...profile, name: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input 
                        type="email"
                        value={profile.email || ''}
                        onChange={(e) => setProfile({...profile, email: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>Phone Number</Label>
                      <Input 
                        value={profile.phone || ''}
                        onChange={(e) => setProfile({...profile, phone: e.target.value})}
                        placeholder="+1 (787) 000-0000"
                      />
                    </div>
                    <Button onClick={handleSaveProfile} disabled={savingProfile}>
                      {savingProfile && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                      Save Changes
                    </Button>
                  </div>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>

      {/* Cancel Booking Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-600" />
              Cancel Booking
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-muted-foreground">
              Are you sure you want to cancel your booking for{' '}
              <strong>{cancelBooking?.tour?.title}</strong>?
            </p>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-sm text-yellow-800">
                <strong>Cancellation Policy:</strong> Free cancellation up to 24 hours before the tour.
              </p>
            </div>
            
            <div>
              <Label>Reason for cancellation (optional)</Label>
              <Textarea 
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Let us know why you're cancelling..."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCancelDialog(false)}>
              Keep Booking
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleCancelBooking}
              disabled={cancelling}
            >
              {cancelling && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Yes, Cancel Booking
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Card Dialog */}
      <Dialog open={showAddCardDialog} onOpenChange={setShowAddCardDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add Payment Method</DialogTitle>
          </DialogHeader>
          {clientSecret && (
            <Elements stripe={stripePromise} options={{ clientSecret }}>
              <AddCardForm 
                clientSecret={clientSecret}
                onSuccess={() => {
                  setShowAddCardDialog(false);
                  setClientSecret('');
                  loadPaymentMethods();
                  toast.success('Card added successfully!');
                }}
                onCancel={() => {
                  setShowAddCardDialog(false);
                  setClientSecret('');
                }}
              />
            </Elements>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Booking Card Component
function BookingCard({ booking, onCancel, isPast, formatDate, getStatusBadge }) {
  const [expanded, setExpanded] = useState(false);
  
  // Count unsigned waivers
  const unsignedWaivers = booking.guests?.filter(g => !g.waiver_signed) || [];
  
  return (
    <Card className="overflow-hidden">
      <div className="p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Tour Image */}
          {booking.tour?.images?.[0] && (
            <div className="w-full sm:w-32 h-24 rounded-lg overflow-hidden flex-shrink-0">
              <img 
                src={booking.tour.images[0]} 
                alt={booking.tour?.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}
          
          {/* Booking Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2 mb-2">
              <h3 className="font-semibold text-lg truncate">{booking.tour?.title || 'Tour'}</h3>
              {getStatusBadge(booking)}
            </div>
            
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm text-muted-foreground mb-3">
              <div className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                <span>{formatDate(booking.date)}</span>
              </div>
              <div className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                <span>{booking.time || 'TBD'}</span>
              </div>
              <div className="flex items-center gap-1">
                <Users className="h-4 w-4" />
                <span>{booking.guest_count || booking.guests?.length || 1} guests</span>
              </div>
              <div className="flex items-center gap-1">
                <CreditCard className="h-4 w-4" />
                <span>${booking.total_amount?.toFixed(2) || '0.00'}</span>
              </div>
            </div>
            
            {/* Waiver Status Alert */}
            {!isPast && unsignedWaivers.length > 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-2 mb-3">
                <p className="text-sm text-yellow-800 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  <span>{unsignedWaivers.length} waiver(s) need signing</span>
                </p>
              </div>
            )}
            
            {/* Action Buttons */}
            <div className="flex flex-wrap gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setExpanded(!expanded)}
              >
                {expanded ? 'Hide Details' : 'View Details'}
              </Button>
              
              {/* Download Receipt */}
              {booking.payment_status === 'paid' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    try {
                      const response = await customerAPI.downloadReceipt(booking.id);
                      const blob = new Blob([response.data], { type: 'application/pdf' });
                      const url = window.URL.createObjectURL(blob);
                      const link = document.createElement('a');
                      link.href = url;
                      link.download = `receipt_${booking.reference_number || booking.id.slice(0,5)}.pdf`;
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                      window.URL.revokeObjectURL(url);
                    } catch (error) {
                      toast.error('Failed to download receipt');
                    }
                  }}
                  data-testid={`download-receipt-${booking.id}`}
                >
                  <FileText className="h-4 w-4 mr-1" />
                  Receipt
                </Button>
              )}
              
              {!isPast && booking.can_cancel && (
                <Button 
                  variant="outline" 
                  size="sm"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={onCancel}
                >
                  <XCircle className="h-4 w-4 mr-1" />
                  Cancel
                </Button>
              )}
            </div>
          </div>
        </div>
        
        {/* Expanded Details */}
        {expanded && (
          <div className="mt-4 pt-4 border-t space-y-4">
            {/* Payment Info */}
            <div>
              <h4 className="font-medium mb-2 flex items-center gap-2">
                <CreditCard className="h-4 w-4" />
                Payment Details
              </h4>
              <div className="bg-muted rounded-lg p-3 text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Reference</span>
                  <span className="font-mono">{booking.reference_number || booking.id?.slice(0, 8).toUpperCase()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Total Paid</span>
                  <span className="font-semibold">${booking.total_amount?.toFixed(2) || '0.00'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Payment Status</span>
                  <span className={booking.payment_status === 'paid' ? 'text-green-600' : 'text-yellow-600'}>
                    {booking.payment_status === 'paid' ? 'Paid' : 'Pending'}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Guests & Waivers */}
            <div>
              <h4 className="font-medium mb-2 flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Guests & Waivers
              </h4>
              <div className="space-y-2">
                {booking.guests?.map((guest, idx) => (
                  <div 
                    key={idx} 
                    className="flex items-center justify-between bg-muted rounded-lg p-3"
                  >
                    <div>
                      <p className="font-medium">{guest.full_name}</p>
                      <p className="text-xs text-muted-foreground">DOB: {guest.date_of_birth}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {guest.waiver_signed ? (
                        <Badge className="bg-green-100 text-green-800 gap-1">
                          <CheckCircle className="h-3 w-3" />
                          Signed
                        </Badge>
                      ) : (
                        <a 
                          href={`/waiver/${booking.id}?guest=${encodeURIComponent(guest.full_name)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Button size="sm" variant="outline" className="gap-1">
                            <FileText className="h-3 w-3" />
                            Sign Waiver
                            <ExternalLink className="h-3 w-3" />
                          </Button>
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Pickup Location */}
            {booking.pickup_location && (
              <div>
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  Pickup Location
                </h4>
                <p className="text-sm text-muted-foreground bg-muted rounded-lg p-3">
                  {booking.pickup_location}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}


// Add Card Form Component (uses Stripe Elements)
function AddCardForm({ clientSecret, onSuccess, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setError(submitError.message);
        setLoading(false);
        return;
      }

      const { error: confirmError, setupIntent } = await stripe.confirmSetup({
        elements,
        clientSecret,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: 'if_required',
      });

      if (confirmError) {
        setError(confirmError.message);
      } else if (setupIntent?.status === 'succeeded') {
        onSuccess();
      } else {
        setError('Card setup did not complete. Please try again.');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="p-4 border rounded-lg bg-white">
        <CardElement 
          options={{
            style: {
              base: {
                fontSize: '16px',
                color: '#424770',
                '::placeholder': { color: '#aab7c4' },
              },
              invalid: { color: '#9e2146' },
            },
          }}
        />
      </div>
      
      {error && (
        <div className="p-3 bg-destructive/10 text-destructive text-sm rounded-lg">
          {error}
        </div>
      )}
      
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button type="submit" disabled={!stripe || loading} data-testid="save-card-btn">
          {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
          Save Card
        </Button>
      </div>
    </form>
  );
}
